import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/** A pending confirmation shown by the shell's confirm dialog. */
export interface ConfirmRequest {
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  /** Paints the confirm action in the danger colour (destructive change). */
  danger: boolean;
}

/**
 * App-wide confirmation dialog. Services ask through `ask()` and get a single
 * boolean back; the shell renders whatever `request$` holds. Kept dependency-free
 * so any service (including CartService) can inject it without a cycle.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmService {
  private readonly _request = new BehaviorSubject<ConfirmRequest | null>(null);
  readonly request$ = this._request.asObservable();

  private answer: Subject<boolean> | null = null;

  /** Open the dialog; the returned stream emits the answer once, then completes. */
  ask(opts: Partial<ConfirmRequest> & { message: string }): Observable<boolean> {
    this.resolve(false); // a second ask supersedes any dialog still open
    const answer = new Subject<boolean>();
    this.answer = answer;
    this._request.next({ title: '', confirmText: 'Continue', cancelText: 'Cancel', danger: false, ...opts });
    return answer.asObservable();
  }

  /** Answer the open dialog (the shell calls this from the buttons / scrim). */
  resolve(ok: boolean): void {
    const answer = this.answer;
    if (!answer) return;
    this.answer = null;
    this._request.next(null);
    answer.next(ok);
    answer.complete();
  }
}
